/**
 * usePdfViewer Hook
 * Custom hook for managing PDF generation and viewing
 */
import { useState, useCallback, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { ResultData } from '../types/results';
import { generateProfilePdf, generatePdfBlobUrl, downloadProfilePdf } from '../utils/pdfGenerator';

interface UsePdfViewerReturn {
  showPdfModal: boolean;
  pdfUrl: string | null;
  generatingPdf: boolean;
  openPdfViewer: () => void;
  closePdfModal: () => void;
  downloadPdf: () => void;
}

export const usePdfViewer = (results: ResultData | null): UsePdfViewerReturn => {
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Cleanup PDF URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  // Open PDF viewer modal
  const openPdfViewer = useCallback(() => {
    if (!results?.profile_analysis) return;
    
    setGeneratingPdf(true);
    
    // Small delay to show loading state
    setTimeout(() => {
      const pdf = generateProfilePdf({
        results,
        profileText: results.profile_analysis!
      });
      
      if (pdf) {
        const url = generatePdfBlobUrl(pdf);
        setPdfUrl(url);
        setShowPdfModal(true);
      }
      setGeneratingPdf(false);
    }, 100);
  }, [results]);

  // Close modal and cleanup
  const closePdfModal = useCallback(() => {
    setShowPdfModal(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  }, [pdfUrl]);

  // Download PDF
  const downloadPdf = useCallback(() => {
    if (!results?.profile_analysis) return;
    
    const pdf = generateProfilePdf({
      results,
      profileText: results.profile_analysis
    });
    
    if (pdf) {
      downloadProfilePdf(pdf, results.version);
    }
  }, [results]);

  return {
    showPdfModal,
    pdfUrl,
    generatingPdf,
    openPdfViewer,
    closePdfModal,
    downloadPdf
  };
};
